import { Box } from '@mantine/core';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import type { ButtonVariants } from '@repo/foundations';
import React from 'react';
import { useCurrentTeam } from '../../../api/hooks/team/myMemberships';

interface TeamSelectorProps {
	onChange?: (value: string) => void;
	initialValue?: string;
	forceVariant?: ButtonVariants;
	allowEmptyOption?: boolean;
	onlyWriteTeams?: boolean;
}

export function TeamSelector({
	onChange,
	initialValue,
	forceVariant,
	onlyWriteTeams = true,
	allowEmptyOption = false,
}: TeamSelectorProps) {
	const { data: teams } = useApiListTeamsV2({
		queryParams: {
			only_write: onlyWriteTeams,
		},
	});

	const { currentTeamId } = useCurrentTeam();

	const [selected, setSelected] = React.useState<string>(
		initialValue ?? currentTeamId ?? ''
	);

	const options: SelectablePropertyItem[] = [
		...(allowEmptyOption
			? [
					{
						label: 'All teams',
						value: '',
						iconName: 'users',
					} as SelectablePropertyItem,
				]
			: []),
		...(teams?.map((t: TeamOut) => ({
			label: t.name,
			value: t.id,
			icon: t.icon,
		})) ?? []),
	];

	const handleChange = (value: string) => {
		setSelected(value);
		onChange?.(value);
	};

	let variant: ButtonVariants = 'default';

	if (forceVariant) {
		variant = forceVariant;
	}

	return (
		<Box maw={512}>
			<SingleSelector
				variant={variant}
				iconType="emoji"
				isViewerUser={false}
				searchable
				options={options}
				placeholder="Team"
				placeholderIconName="users"
				onChange={handleChange}
				initialSelected={selected ?? currentTeamId ?? ''}
				property="team"
			/>
		</Box>
	);
}
